import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../base-query';
import type {
  AddKnowledgeUrlBody,
  KnowledgeDocument,
  KnowledgeSource,
  UpdateKnowledgeSourceBody,
  UploadKnowledgeBody,
} from '@/types/knowledge';

/**
 * The customer knowledge base API (CLE-495): cleanse-api
 * `/api/v1/knowledge`. Writes are admin-only on the server; the source list
 * refreshes after each.
 */
export const knowledgeApi = createApi({
  reducerPath: 'knowledgeApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['KnowledgeSources', 'KnowledgeDocuments'],
  endpoints: (builder) => ({
    listKnowledgeSources: builder.query<KnowledgeSource[], void>({
      query: () => '/knowledge/sources',
      providesTags: ['KnowledgeSources'],
    }),
    listKnowledgeDocuments: builder.query<KnowledgeDocument[], string>({
      query: (sourceId) => `/knowledge/sources/${sourceId}/documents`,
      providesTags: (_result, _error, sourceId) => [
        { type: 'KnowledgeDocuments', id: sourceId },
      ],
    }),
    addKnowledgeUrl: builder.mutation<KnowledgeSource, AddKnowledgeUrlBody>({
      query: ({ site, ...body }) => ({
        url: `/knowledge/sources/${site ? 'site' : 'url'}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['KnowledgeSources'],
    }),
    // Google Docs the admin picked with their own Google Docs connection.
    addKnowledgeGoogleDocs: builder.mutation<
      KnowledgeSource,
      { fileIds: string[]; name?: string }
    >({
      query: (body) => ({
        url: '/knowledge/sources/google-docs',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['KnowledgeSources'],
    }),
    uploadKnowledgeFiles: builder.mutation<
      KnowledgeSource,
      UploadKnowledgeBody
    >({
      query: ({ files, name }) => {
        const form = new FormData();
        for (const file of files) form.append('files', file);
        if (name) form.append('name', name);
        return { url: '/knowledge/sources/upload', method: 'POST', body: form };
      },
      invalidatesTags: ['KnowledgeSources'],
    }),
    updateKnowledgeSource: builder.mutation<
      KnowledgeSource,
      UpdateKnowledgeSourceBody
    >({
      query: ({ id, ...body }) => ({
        url: `/knowledge/sources/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['KnowledgeSources'],
      // The saved source goes into the list at once, so a second change
      // made before the refetch lands builds on this one, not the old grants.
      async onQueryStarted(_body, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            knowledgeApi.util.updateQueryData(
              'listKnowledgeSources',
              undefined,
              (sources) => sources.map((s) => (s.id === data.id ? data : s))
            )
          );
        } catch {
          // Refused: the caller says why, and the list keeps the old grants.
        }
      },
    }),
    syncKnowledgeSource: builder.mutation<KnowledgeSource, string>({
      query: (id) => ({ url: `/knowledge/sources/${id}/sync`, method: 'POST' }),
      invalidatesTags: (_result, _error, id) => [
        'KnowledgeSources',
        { type: 'KnowledgeDocuments', id },
      ],
    }),
    removeKnowledgeSource: builder.mutation<KnowledgeSource, string>({
      query: (id) => ({ url: `/knowledge/sources/${id}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, id) => [
        'KnowledgeSources',
        { type: 'KnowledgeDocuments', id },
      ],
    }),
  }),
});

export const {
  useListKnowledgeSourcesQuery,
  useListKnowledgeDocumentsQuery,
  useAddKnowledgeUrlMutation,
  useAddKnowledgeGoogleDocsMutation,
  useUploadKnowledgeFilesMutation,
  useUpdateKnowledgeSourceMutation,
  useSyncKnowledgeSourceMutation,
  useRemoveKnowledgeSourceMutation,
} = knowledgeApi;
